import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import {
  ColorOptionLabel,
  MiBox,
  MiDetailFields,
  MiDetailPageHeader,
  MiTab,
} from "@miplatform/components";
import {
  ASSIGNMENT_TEMPLATE_TABS,
  ASSIGNMENT_TEMPLATE_TABS_LIST,
  ASSIGNMENT_TEMPLATE_TYPES,
  CONFIGURABLE_COLORS,
  INPUT_TYPES,
} from "@miplatform/constants";
import { useEdit, useRouter } from "@miplatform/hooks";
import { mdiChevronLeft, mdiFormatListBulleted } from "@mdi/js";
import { Footer } from "@mibuildweb/components";
import { Box } from "@mui/material";
import { useAssignmentTemplateViewModel } from "../useAssignmentTemplateViewModel";
import { IssueTemplatesTab } from "./IssueTemplatesTab";
import { StepTemplatesTab } from "./StepTemplatesTab";
import { useAssignmentTemplateDetailsViewModel } from "./useAssignmentTemplateDetailsViewModel";

export function AssignmentTemplateDetails() {
  const router = useRouter();
  const { id, activeTab = ASSIGNMENT_TEMPLATE_TABS.DETAILS } = router.params;
  const [type, setType] = useState(null);
  const {
    loading,
    assignmentTemplate,
    updateAssignmentTemplate,
    getAssignmentTemplateDetails,
    getIssueTemplates,
    issueTemplates,
    createStepTemplateItem,
    updateStepTemplateItem,
    deleteStepTemplateItem,
    assignmentTemplateOptions,
    getAssignmentTemplateOptions,
    getResourceGroups,
    resourceGroups,
  } = useAssignmentTemplateDetailsViewModel(id);

  const { getRoleOptions, roleOptions } = useAssignmentTemplateViewModel();

  const { classes } = useStyles();

  useEffect(() => {
    getAssignmentTemplateDetails(id);
    getIssueTemplates();
    getAssignmentTemplateOptions();
    getResourceGroups();
    getRoleOptions();
  }, []);

  useEffect(() => {
    if (assignmentTemplate.type) {
      setType(assignmentTemplate.type);
    }
  }, [assignmentTemplate.type]);

  const detailFields = [
    {
      label: "Name",
      name: "name",
      width: "195px",
      validationRule: z
        .string()
        .min(1, "Assignment Template name is required."),
      required: true,
    },
    {
      label: "Short Name",
      name: "shortName",
      width: "195px",
      validationRule: z
        .string()
        .min(1, "Assignment Template short name is required."),
      required: true,
    },
    {
      label: "Type",
      name: "type",
      width: "195px",
      fieldType: INPUT_TYPES.SELECT,
      options: Object.values(ASSIGNMENT_TEMPLATE_TYPES).map((item) => ({
        label: item,
        value: item,
      })),
      validationRule: z
        .string()
        .min(1, "Assignment Template type is required."),
      required: true,
      afterOnChange: (value) => setType(value),
    },
    {
      label: "Geofence Distance",
      name: "geoFenceDistance",
      width: "195px",
      fieldType: INPUT_TYPES.NUMBER,
    },
    {
      label: "Schedule Time Threshold (hours)",
      name: "scheduleTimeThreshold",
      width: "195px",
      fieldType: INPUT_TYPES.NUMBER,
      validationRule: z
        .number()
        .min(0.0, "Schedule Time Threshold cannot be negative")
        .nullable(),
    },
    {
      label: "Color",
      name: "color",
      fieldType: INPUT_TYPES.SELECT,
      options: CONFIGURABLE_COLORS.map(({ color, label }) => ({
        label: <ColorOptionLabel color={color} label={label} />,
        value: color,
      })),
      width: "195px",
      validationRule: z.string().min(1, "Color is required"),
      required: true,
    },
    {
      label: "Description",
      name: "description",
      fieldType: INPUT_TYPES.TEXTAREA,
      width: "80%",
      validationRule: z.string().min(1, "Description is required"),
      required: true,
    },
    {
      label: "Assignable Resource Groups",
      name: "qualificationIds",
      options: resourceGroups,
      fieldType: INPUT_TYPES.MULTI_SELECT,
      width: "50%",
      minWidth: "180px",
      required: true,
      validationRule: z.array(z.string().optional()).optional(),
    },
    {
      label: "Auto Assign Resource Groups",
      name: "autoAssignmentQualificationId",
      width: "50%",
      minWidth: "180px",
      fieldType: INPUT_TYPES.SELECT,
    },

    {
      label: "Allowable Schedule Limit (Days)",
      name: "allowableScheduleLimit",
      width: "50%",
      minWidth: "180px",
      fieldType: INPUT_TYPES.NUMBER,
      validationRule: z
        .number()
        .min(0, "Allowable Schedule Limit (Days) must be greater than 0"),
    },
    {
      label: "Allowable Reattempt Schedule Limit (Days)",
      name: "allowableReattemptScheduleLimit",
      width: "50%",
      minWidth: "180px",
      fieldType: INPUT_TYPES.NUMBER,
      validationRule: z
        .number()
        .min(0, "Allowable Reattempt Schedule Limit must be greater than 0"),
    },
    {
      label: "Roles Allowed to Assign",
      name: "roleIdsAllowedToAssign",
      options: roleOptions,
      fieldType: INPUT_TYPES.MULTI_SELECT,
      width: "50%",
      required: true,
      validationRule: z.array(z.string().optional()).optional(),
    },
    {
      label: "Is user photo required",
      name: "isUserPhotoRequired",
      width: "25%",
      fieldType: INPUT_TYPES.SWITCH,
    },
    {
      label: "Show Pending Button",
      name: "showPendingButton",
      width: "25%",
      fieldType: INPUT_TYPES.SWITCH,
    },
  ];

  let updatedDetailFields =
    type === ASSIGNMENT_TEMPLATE_TYPES.REVIEW
      ? [
          ...detailFields.slice(0, detailFields.length - 1),
          {
            label: "Reviewing Assignment Template",
            fieldType: INPUT_TYPES.MULTI_SELECT,
            name: "assignmentTemplateIdsToBeReviewed",
            width: "400px",
            options: assignmentTemplateOptions,
          },
          detailFields[detailFields.length - 1],
        ]
      : detailFields;

  const edit = useEdit(assignmentTemplate, updatedDetailFields);

  updatedDetailFields = updatedDetailFields.map((item) => {
    if (item.name !== "autoAssignmentQualificationId") return item;

    item.options = edit.getValue("qualificationIds")?.map((id) => {
      const resourceGroup = resourceGroups.find(({ value }) => value === id);
      return {
        label: resourceGroup?.label,
        value: resourceGroup?.value,
      };
    });

    return item;
  });

  const resetFormData = () => {
    setType(assignmentTemplate.type);
    edit.reset();
  };

  const onUpdate = async (data) => {
    const response = await updateAssignmentTemplate(id, data);
    if (response) {
      getAssignmentTemplateDetails(id);
      edit.reset();
    }
  };

  const handleIssueTemplateUpdate = async (value) => {
    await updateAssignmentTemplate(id, {
      issueTemplateIds: value,
      modifiedOn: assignmentTemplate.modifiedOn,
    });
  };

  const handleTabChange = (tab) => {
    router.navigate(
      `/settings/order-management/assignment-templates/${id}/${tab}`
    );
  };

  const handleStepTemplateUpdate = (data) =>
    updateStepTemplateItem({
      stepTemplateId: data.id,
      assignmentTemplateId: id,
      data,
    });

  const handleStepTemplateCreation = (data) => createStepTemplateItem(id, data);

  const handleStepTemplateDeletion = (data) => {
    deleteStepTemplateItem({
      assignmentTemplateId: id,
      stepTemplateId: data.id,
      data,
    });
  };

  const distinctIssueTemplates = !assignmentTemplate?.issueTemplate?.length
    ? issueTemplates
    : issueTemplates.filter(
        (item) =>
          !assignmentTemplate.issueTemplates
            .map((i) => i.id)
            .includes(item.value)
      );

  const customOnBack = () => {
    router.navigate(`/settings/order-management/assignment-templates`);
  };

  return (
    <Box className={classes.contentContainer}>
      <MiDetailPageHeader
        title={`Assignment Template: ${assignmentTemplate?.name || ""}`}
        titleIcon={mdiChevronLeft}
        customOnBack={customOnBack}
        loading={loading}
      />
      <MiTab
        tabs={ASSIGNMENT_TEMPLATE_TABS_LIST}
        currentTab={activeTab}
        onTabChange={handleTabChange}
      />
      <MiBox className={classes.container}>
        {activeTab === ASSIGNMENT_TEMPLATE_TABS.DETAILS && (
          <MiDetailFields
            detailFields={updatedDetailFields}
            headerProps={{
              icon: mdiFormatListBulleted,
              title: "Detail Fields",
            }}
            edit={edit}
          />
        )}
        {activeTab === ASSIGNMENT_TEMPLATE_TABS.STEP_TEMPLATES && (
          <StepTemplatesTab
            assignmentTemplateId={id}
            stepTemplates={assignmentTemplate.stepTemplates || []}
            updateItem={handleStepTemplateUpdate}
            createItem={handleStepTemplateCreation}
            handleDelete={handleStepTemplateDeletion}
          />
        )}
        {activeTab === ASSIGNMENT_TEMPLATE_TABS.ISSUE_TEMPLATES && (
          <IssueTemplatesTab
            issueTemplates={distinctIssueTemplates}
            onUpdate={handleIssueTemplateUpdate}
            currentIssueTemplates={assignmentTemplate?.issueTemplates}
          />
        )}
      </MiBox>
      <Footer
        show={edit.isDirty()}
        handleCancelClick={resetFormData}
        handleSaveClick={() => onUpdate(edit.handleSubmit())}
        disableSave={!edit.isValid()}
      />
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    overflowY: "scroll",
    backgroundColor: theme.palette.common.white,
  },
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
  },
}));
